import { useMemo, useState } from 'react';

import { useClinicalFunnel } from '@/lib/api/clinicalAdminHooks';

import { TicketQueues } from '../support/TicketQueues';

import { Card, ErrorState, LoadingState } from './ui';

type DateRange = {
  from: string;
  through: string;
};

const DATE_INPUT_CLASS =
  'type-label-sm px-10 py-8 rounded border border-line bg-raised';

function inputDate(value: Date): string {
  const year = value.getFullYear();
  const month = String(value.getMonth() + 1).padStart(2, '0');
  const day = String(value.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function thisMonth(): DateRange {
  const now = new Date();
  return {
    from: inputDate(new Date(now.getFullYear(), now.getMonth(), 1)),
    through: inputDate(now),
  };
}

function lastMonth(): DateRange {
  const now = new Date();
  return {
    from: inputDate(new Date(now.getFullYear(), now.getMonth() - 1, 1)),
    through: inputDate(new Date(now.getFullYear(), now.getMonth(), 0)),
  };
}

function lastThirtyDays(): DateRange {
  const now = new Date();
  const start = new Date(now);
  start.setDate(start.getDate() - 29);
  return { from: inputDate(start), through: inputDate(now) };
}

export function ClinicalAdminReportingScreen() {
  const initial = useMemo(thisMonth, []);
  const [dateFrom, setDateFrom] = useState(initial.from);
  const [dateThrough, setDateThrough] = useState(initial.through);
  const [generatedRange, setGeneratedRange] = useState<DateRange | null>(null);
  const [operationalRange, setOperationalRange] = useState<DateRange | null>(
    null,
  );
  const report = useClinicalFunnel(
    generatedRange?.from ?? '',
    generatedRange?.through ?? '',
    generatedRange !== null,
  );

  const generate = () => {
    if (!dateFrom || !dateThrough || dateFrom > dateThrough) return;
    if (
      generatedRange?.from === dateFrom &&
      generatedRange.through === dateThrough
    ) {
      void report.refetch();
      return;
    }
    setGeneratedRange({ from: dateFrom, through: dateThrough });
  };

  const applyRange = (range: DateRange) => {
    setDateFrom(range.from);
    setDateThrough(range.through);
  };

  const invalidRange = !dateFrom || !dateThrough || dateFrom > dateThrough;
  const stages = report.data
    ? [
        { label: 'Signed up', value: report.data.signed_up },
        { label: 'Intake complete', value: report.data.intake_complete },
        { label: 'Rx written', value: report.data.rx_written },
        { label: 'Pharmacy shipped', value: report.data.shipped },
        { label: 'Delivered', value: report.data.delivered },
      ]
    : [];
  const base = report.data?.signed_up || 1;

  return (
    <div className="flex flex-col gap-24">
      <div className="flex flex-col gap-8">
        <p className="type-label-sm text-ink-muted">Clinical Admin</p>
        <h1 className="type-heading-xl">Reporting</h1>
        <p className="type-body-md text-ink-muted max-w-content-sm">
          Generate operational reports only when they are needed. Reports do
          not run while browsing Clinical Admin Overview or the customer CRM.
        </p>
      </div>

      <Card>
        <div className="p-16 flex flex-col gap-16">
          <div className="flex flex-col gap-4">
            <h2 className="type-heading-md">Patient funnel</h2>
            <p className="type-body-sm text-ink-muted">
              Select a signup cohort. Stage counts show the current progress of
              patients who joined during that date range.
            </p>
          </div>

          <div className="flex flex-wrap gap-8">
            <button
              type="button"
              onClick={() => applyRange(thisMonth())}
              className="type-label-sm px-10 py-6 rounded border border-line hover:bg-raised"
            >
              This month
            </button>
            <button
              type="button"
              onClick={() => applyRange(lastMonth())}
              className="type-label-sm px-10 py-6 rounded border border-line hover:bg-raised"
            >
              Last month
            </button>
            <button
              type="button"
              onClick={() => applyRange(lastThirtyDays())}
              className="type-label-sm px-10 py-6 rounded border border-line hover:bg-raised"
            >
              Last 30 days
            </button>
          </div>

          <div className="flex flex-wrap items-end gap-12">
            <label className="flex flex-col gap-4 type-label-sm text-ink-muted">
              From
              <input
                type="date"
                value={dateFrom}
                onChange={(event) => setDateFrom(event.target.value)}
                className={DATE_INPUT_CLASS}
              />
            </label>
            <label className="flex flex-col gap-4 type-label-sm text-ink-muted">
              Through
              <input
                type="date"
                value={dateThrough}
                onChange={(event) => setDateThrough(event.target.value)}
                className={DATE_INPUT_CLASS}
              />
            </label>
            <button
              type="button"
              disabled={invalidRange || report.isFetching}
              onClick={generate}
              className="type-label-sm px-14 py-9 rounded bg-ink text-page disabled:opacity-40"
            >
              {report.isFetching ? 'Generating…' : 'Generate funnel'}
            </button>
          </div>
          {invalidRange && (
            <p className="type-label-sm text-danger">
              Choose a valid start date on or before the end date.
            </p>
          )}
        </div>
      </Card>

      {report.isLoading && <LoadingState label="Generating funnel…" />}
      {report.isError && (
        <ErrorState>Could not generate the funnel report.</ErrorState>
      )}
      {report.data && generatedRange && (
        <div className="flex flex-col gap-10">
          <p className="type-label-sm text-ink-muted">
            Signup cohort {generatedRange.from} through {generatedRange.through}
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 border border-line rounded-lg bg-raised overflow-hidden">
            {stages.map((stage) => (
              <div
                key={stage.label}
                className="px-16 py-14 border-b sm:border-r border-line last:border-r-0"
              >
                <div className="type-heading-lg">
                  {stage.value.toLocaleString()}
                </div>
                <div className="type-label-sm text-ink">{stage.label}</div>
                <div className="type-10 text-ink-muted">
                  {Math.round((stage.value / base) * 100)}% of signed up
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <Card>
        <div className="p-16 flex flex-col gap-12">
          <div className="flex flex-col gap-4">
            <h2 className="type-heading-md">Current operational queues</h2>
            <p className="type-body-sm text-ink-muted">
              Load the active order lifecycle and support-ticket backlog only
              when you need to work or inspect it. This report scans the full
              current backlog and does not run from the Overview page.
            </p>
          </div>
          <button
            type="button"
            disabled={invalidRange}
            onClick={() =>
              setOperationalRange({ from: dateFrom, through: dateThrough })
            }
            className="type-label-sm px-14 py-9 rounded bg-ink text-page self-start disabled:opacity-40"
          >
            Generate operational report
          </button>
        </div>
      </Card>

      {operationalRange && (
        <TicketQueues
          key={`${operationalRange.from}:${operationalRange.through}`}
          dateFrom={operationalRange.from}
          dateThrough={operationalRange.through}
        />
      )}
    </div>
  );
}
